<script>
// App-level two-factor (TOTP) enrollment + management modal.
// Opened from the Settings modal (and anywhere) via the
// `worksched:open-2fa` window event. Steps:
//   loading → enroll (QR + confirm) → codes (recovery codes shown once)
//   manage (when already enabled) → password (disable / regenerate) → codes
import {
  enableTwoFactor,
  confirmTwoFactor,
  disableTwoFactor,
  regenerateRecoveryCodes,
} from "@/services/auth.service";

export default {
  data() {
    return {
      show: false,
      forced: false,
      step: "loading",
      mode: "", // 'disable' | 'regenerate' (for the password step)
      processing: false,
      error: "",
      qrSvg: "",
      secret: "",
      code: "",
      password: "",
      recoveryCodes: [],
      copied: false,
    };
  },
  computed: {
    currentUser() {
      return this.$store.state.auth.currentUser;
    },
    enabled() {
      return !!this.currentUser?.two_factor_enabled;
    },
    // Re-evaluate forced enrollment whenever identity or the 2FA flags change.
    guardKey() {
      const u = this.currentUser;
      return u
        ? `${u.id}|${u.two_factor_required ? 1 : 0}|${u.two_factor_enabled ? 1 : 0}|${u.must_change_password ? 1 : 0}`
        : "";
    },
  },
  watch: {
    show(v) {
      if (typeof document !== "undefined") document.body.style.overflow = v ? "hidden" : "";
    },
    guardKey() {
      this.evaluate();
    },
  },
  mounted() {
    window.addEventListener("worksched:open-2fa", this.open);
    this.evaluate();
  },
  beforeUnmount() {
    window.removeEventListener("worksched:open-2fa", this.open);
    if (typeof document !== "undefined") document.body.style.overflow = "";
  },
  methods: {
    reset() {
      this.error = "";
      this.code = "";
      this.password = "";
      this.recoveryCodes = [];
      this.copied = false;
      this.qrSvg = "";
      this.secret = "";
      this.mode = "";
    },
    open() {
      this.forced = false;
      this.reset();
      this.show = true;
      if (this.enabled) {
        this.step = "manage";
      } else {
        this.startEnroll();
      }
    },
    // Roles in the API's TWO_FACTOR_REQUIRED_ROLES (currently "approver") must
    // enroll before they can use the app — show a non-dismissible wizard. The
    // password-change guard takes precedence, so hold off while it's pending.
    evaluate() {
      const u = this.currentUser;
      if (!u) {
        if (this.forced) {
          this.forced = false;
          this.show = false;
        }
        return;
      }
      if (u.must_change_password) return;
      if (u.two_factor_required && !u.two_factor_enabled) {
        if (this.show && this.forced) return; // already enrolling
        this.forced = true;
        this.reset();
        this.show = true;
        this.startEnroll();
      }
    },
    close() {
      if (this.processing || this.forced) return;
      this.show = false;
    },
    async logoutNow() {
      try {
        await this.$store.dispatch("auth/logOut");
      } finally {
        this.forced = false;
        this.show = false;
        this.$router.push({ name: "login" });
      }
    },
    refreshUser(user) {
      if (user) this.$store.commit("auth/SET_CURRENT_USER", user);
    },
    async startEnroll() {
      this.step = "loading";
      this.error = "";
      this.qrSvg = "";
      try {
        const data = await enableTwoFactor();
        this.qrSvg = data.qr_svg;
        this.secret = data.secret;
        this.step = "enroll";
      } catch (e) {
        this.error = e.response?.data?.message || e.message || "Could not start 2FA setup.";
        // Forced users can't fall back to "manage" (they aren't enrolled) — keep
        // them on the enroll step, which shows a Retry while there's no QR yet.
        this.step = this.forced ? "enroll" : "manage";
      }
    },
    async confirm() {
      this.error = "";
      if (!this.code.trim()) {
        this.error = "Enter the 6-digit code from your app.";
        return;
      }
      this.processing = true;
      try {
        const data = await confirmTwoFactor(this.code.trim());
        this.refreshUser(data.user);
        this.recoveryCodes = data.recovery_codes || [];
        this.step = "codes";
      } catch (e) {
        this.error = e.response?.data?.message || e.message || "Invalid code.";
      } finally {
        this.processing = false;
      }
    },
    openPassword(mode) {
      this.mode = mode;
      this.password = "";
      this.error = "";
      this.step = "password";
    },
    async submitPassword() {
      this.error = "";
      if (!this.password) {
        this.error = "Enter your password.";
        return;
      }
      this.processing = true;
      try {
        if (this.mode === "disable") {
          const user = await disableTwoFactor(this.password);
          this.refreshUser(user);
          this.show = false;
          this.$swal?.fire({ icon: "success", title: "Two-factor disabled", timer: 1400, showConfirmButton: false });
        } else {
          this.recoveryCodes = await regenerateRecoveryCodes(this.password);
          this.step = "codes";
        }
      } catch (e) {
        this.error = e.response?.data?.message || e.message || "Incorrect password.";
      } finally {
        this.processing = false;
      }
    },
    copyCodes() {
      const text = this.recoveryCodes.join("\n");
      try {
        navigator.clipboard.writeText(text);
        this.copied = true;
        setTimeout(() => (this.copied = false), 1800);
      } catch {
        /* clipboard may be blocked; user can still read them */
      }
    },
    downloadCodes() {
      const blob = new Blob([this.recoveryCodes.join("\n") + "\n"], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "worksched-2fa-recovery-codes.txt";
      a.click();
      URL.revokeObjectURL(url);
    },
    doneCodes() {
      this.forced = false;
      this.show = false;
      this.$swal?.fire({ icon: "success", title: "Two-factor authentication is on", timer: 1500, showConfirmButton: false });
    },
  },
};
</script>

<template>
  <Teleport to="body">
    <div v-if="show" class="ws-2fa-backdrop" @click.self="close">
      <div class="ws-2fa-dialog">
        <div class="ws-2fa-card">
          <header class="ws-2fa-header">
            <h5 class="ws-2fa-title"><i class="bx bx-shield-quarter me-1"></i> Two-Factor Authentication</h5>
            <button v-if="!processing && !forced" type="button" class="ws-2fa-close" aria-label="Close" @click="close">
              <i class="bx bx-x"></i>
            </button>
          </header>

          <div class="ws-2fa-body">
            <div v-if="error" class="alert alert-danger py-2 mb-3">{{ error }}</div>

            <!-- Loading -->
            <div v-if="step === 'loading'" class="text-center py-4">
              <span class="spinner-border text-warning"></span>
              <p class="text-muted small mt-2 mb-0">Preparing your setup…</p>
            </div>

            <!-- Enroll: QR + confirm -->
            <template v-else-if="step === 'enroll'">
              <div v-if="forced" class="alert alert-warning py-2 mb-3">
                Your role requires two-factor authentication. Set it up now to continue.
              </div>

              <template v-if="qrSvg">
                <p class="small text-muted mb-2">
                  Scan this QR code with an authenticator app (Google Authenticator, Authy, Microsoft Authenticator),
                  then enter the 6-digit code it shows.
                </p>
                <div class="text-center mb-2">
                  <img :src="qrSvg" alt="2FA QR code" class="ws-2fa-qr" />
                </div>
                <p class="small text-muted mb-1">Can't scan? Enter this key manually:</p>
                <code class="ws-2fa-secret d-block mb-3">{{ secret }}</code>

                <label class="form-label small text-muted mb-1">Authentication code</label>
                <input
                  v-model="code"
                  type="text"
                  class="form-control"
                  placeholder="6-digit code"
                  autocomplete="one-time-code"
                  @keyup.enter="confirm"
                />
              </template>
              <div v-else class="text-center py-3">
                <button type="button" class="btn ws-2fa-primary" @click="startEnroll">
                  <i class="bx bx-refresh me-1"></i> Retry setup
                </button>
              </div>
            </template>

            <!-- Recovery codes -->
            <template v-else-if="step === 'codes'">
              <div class="alert alert-warning py-2 mb-3">
                Save these recovery codes somewhere safe. Each can be used once if you lose your device.
                <b>They won't be shown again.</b>
              </div>
              <div class="ws-2fa-codes">
                <code v-for="c in recoveryCodes" :key="c">{{ c }}</code>
              </div>
              <div class="d-flex gap-2 mt-3">
                <button type="button" class="btn btn-light btn-sm" @click="copyCodes">
                  <i class="bx bx-copy me-1"></i>{{ copied ? "Copied!" : "Copy" }}
                </button>
                <button type="button" class="btn btn-light btn-sm" @click="downloadCodes">
                  <i class="bx bx-download me-1"></i>Download
                </button>
              </div>
            </template>

            <!-- Manage (already enabled) -->
            <template v-else-if="step === 'manage'">
              <p class="mb-3">
                <span class="badge bg-success-subtle text-success"><i class="bx bx-check-shield me-1"></i>Enabled</span>
                Your account is protected with an authenticator app.
              </p>
              <div class="d-grid gap-2">
                <button type="button" class="btn btn-light" @click="openPassword('regenerate')">
                  <i class="bx bx-refresh me-1"></i> Regenerate recovery codes
                </button>
                <button type="button" class="btn btn-outline-danger" @click="openPassword('disable')">
                  <i class="bx bx-shield-x me-1"></i> Disable two-factor
                </button>
              </div>
            </template>

            <!-- Password step (disable / regenerate) -->
            <template v-else-if="step === 'password'">
              <p class="small text-muted mb-2">
                {{ mode === "disable"
                  ? "Confirm your password to turn off two-factor authentication."
                  : "Confirm your password to generate new recovery codes (your old codes stop working)." }}
              </p>
              <label class="form-label small text-muted mb-1">Password</label>
              <input
                v-model="password"
                type="password"
                class="form-control"
                autocomplete="current-password"
                @keyup.enter="submitPassword"
              />
            </template>
          </div>

          <footer class="ws-2fa-footer">
            <template v-if="step === 'enroll'">
              <button v-if="forced" type="button" class="btn btn-link text-muted me-auto" :disabled="processing" @click="logoutNow">Sign out</button>
              <button v-else type="button" class="btn btn-light" :disabled="processing" @click="close">Cancel</button>
              <button v-if="qrSvg" type="button" class="btn ws-2fa-primary" :disabled="processing" @click="confirm">
                <span v-if="processing"><span class="spinner-border spinner-border-sm me-1"></span> Verifying…</span>
                <span v-else>Verify &amp; enable</span>
              </button>
            </template>
            <template v-else-if="step === 'codes'">
              <button type="button" class="btn ws-2fa-primary ms-auto" @click="doneCodes">Done</button>
            </template>
            <template v-else-if="step === 'manage'">
              <button type="button" class="btn btn-light ms-auto" @click="close">Close</button>
            </template>
            <template v-else-if="step === 'password'">
              <button type="button" class="btn btn-light" :disabled="processing" @click="step = 'manage'">Back</button>
              <button
                type="button"
                class="btn"
                :class="mode === 'disable' ? 'btn-danger' : 'ws-2fa-primary'"
                :disabled="processing"
                @click="submitPassword"
              >
                <span v-if="processing"><span class="spinner-border spinner-border-sm me-1"></span> Working…</span>
                <span v-else>{{ mode === "disable" ? "Disable" : "Regenerate" }}</span>
              </button>
            </template>
          </footer>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.ws-2fa-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1070;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}
.ws-2fa-dialog { width: 100%; max-width: 460px; }
.ws-2fa-card {
  background: #fff;
  border-radius: 0.6rem;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 2rem);
  overflow: hidden;
}
.ws-2fa-header {
  background: #F57C00;
  color: #fff;
  padding: 0.85rem 1.25rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.ws-2fa-title { margin: 0; font-size: 1.05rem; font-weight: 600; color: #fff; }
.ws-2fa-close { background: transparent; border: 0; color: #fff; font-size: 1.35rem; line-height: 1; cursor: pointer; }
.ws-2fa-body { padding: 1.25rem; overflow-y: auto; }
.ws-2fa-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  align-items: center;
  padding: 0.85rem 1.25rem;
  border-top: 1px solid #e9ecef;
}
.ws-2fa-primary { background: #F57C00; border: 1px solid #F57C00; color: #fff; }
.ws-2fa-primary:hover, .ws-2fa-primary:focus { background: #EA580C; border-color: #EA580C; color: #fff; }

.ws-2fa-qr { width: 200px; height: 200px; border: 1px solid #e9ecef; border-radius: 0.5rem; padding: 6px; background: #fff; }
.ws-2fa-secret {
  background: #f8f9fa; border: 1px solid #e9ecef; border-radius: 0.4rem;
  padding: 0.5rem 0.75rem; font-size: 0.95rem; letter-spacing: 0.08em; color: #212529; word-break: break-all;
}
.ws-2fa-codes {
  display: grid; grid-template-columns: 1fr 1fr; gap: 0.4rem 0.75rem;
  background: #f8f9fa; border: 1px solid #e9ecef; border-radius: 0.5rem; padding: 0.75rem 1rem;
}
.ws-2fa-codes code { color: #212529; font-size: 0.95rem; letter-spacing: 0.04em; }
</style>
